import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Calendar, ArrowRight, Trash2, PlayCircle, Pencil } from "lucide-react";

const EMPTY_REPLAY = { title: "", date: new Date().toISOString().slice(0, 10), video_url: "", description: "", week_number: "" };

export default function CoachMTM() {
  const [tab, setTab] = useState("weeks");
  const [weeks, setWeeks] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [clients, setClients] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [replays, setReplays] = useState([]);
  const [showReplay, setShowReplay] = useState(false);
  const [replayEditId, setReplayEditId] = useState(null);
  const [replayForm, setReplayForm] = useState(EMPTY_REPLAY);

  const [newCohort, setNewCohort] = useState({ name: "", start_date: new Date().toISOString().slice(0, 10) });
  const [showCohort, setShowCohort] = useState(false);

  const [newSupp, setNewSupp] = useState({ name: "", brand: "", purpose: "", affiliate_url: "", discount_code: "", category: "supplement" });
  const [showSupp, setShowSupp] = useState(false);
  const [seats, setSeats] = useState({ total: 20, taken: 0 });

  const load = useCallback(async () => {
    const [w, c, cl, s, st, rp] = await Promise.all([
      api.get("/mtm/weeks"),
      api.get("/mtm/cohorts"),
      api.get("/coach/clients"),
      api.get("/supplements"),
      api.get("/public/mtm-seats"),
      api.get("/mtm/replays"),
    ]);
    setWeeks(w.data); setCohorts(c.data); setClients(cl.data.assigned || []); setSupplements(s.data);
    setSeats({ total: st.data.total, taken: st.data.taken });
    setReplays(rp.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const weeksByPhase = useMemo(
    () => Object.fromEntries(["Detox", "Restore", "Burn", "Reset"].map((p) => [p, weeks.filter((w) => w.phase === p)])),
    [weeks]
  );

  const saveSeats = async () => {
    try {
      await api.put("/settings/mtm-seats", { total_seats: seats.total, seats_taken: seats.taken });
      toast.success("Early-bird seats updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createCohort = async (e) => {
    e.preventDefault();
    try { await api.post("/mtm/cohorts", newCohort); toast.success("Cohort created"); setShowCohort(false); setNewCohort({ name: "", start_date: new Date().toISOString().slice(0, 10) }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const enroll = async (user_id, cohort_id) => {
    if (!cohort_id) return;
    try { await api.post("/mtm/enroll", { user_id, cohort_id }); toast.success("Enrolled"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createSupp = async (e) => {
    e.preventDefault();
    try { await api.post("/supplements", newSupp); toast.success("Added"); setShowSupp(false); setNewSupp({ name: "", brand: "", purpose: "", affiliate_url: "", discount_code: "", category: "supplement" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteSupp = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/supplements/${id}`); load();
  };

  const submitReplay = async (e) => {
    e.preventDefault();
    const payload = {
      title: replayForm.title, date: replayForm.date, video_url: replayForm.video_url,
      description: replayForm.description || null,
      week_number: replayForm.week_number ? parseInt(replayForm.week_number, 10) : null,
    };
    try {
      if (replayEditId) await api.patch(`/mtm/replays/${replayEditId}`, payload);
      else await api.post("/mtm/replays", payload);
      toast.success(replayEditId ? "Replay updated" : "Replay added");
      setReplayForm(EMPTY_REPLAY); setReplayEditId(null); setShowReplay(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const editReplay = (r) => {
    setReplayEditId(r.replay_id);
    setReplayForm({ title: r.title, date: r.date, video_url: r.video_url, description: r.description || "", week_number: r.week_number || "" });
    setShowReplay(true);
  };

  const deleteReplay = async (id) => {
    if (!window.confirm("Delete this replay?")) return;
    await api.delete(`/mtm/replays/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-10" data-testid="coach-mtm">
      <header>
        <p className="overline">Metabolic Transformation Method</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
          MTM <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>command center</span>
        </h1>
        <div className="bronze-rule mt-6" />
      </header>

      <div className="card-luxe flex items-end gap-5 flex-wrap" data-testid="seats-editor">
        <div>
          <div className="overline">Landing page · Early-bird seats counter</div>
          <p className="text-xs text-secondary-luxe mt-2 max-w-md">Shown on the MTM offer card. Payments via Stripe auto-fill a seat; adjust manually anytime.</p>
        </div>
        <div>
          <label className="label-luxe">Total seats</label>
          <input type="number" min="0" className="input-luxe w-24" value={seats.total}
            onChange={(e) => setSeats({ ...seats, total: parseInt(e.target.value || "0", 10) })} data-testid="seats-total-input" />
        </div>
        <div>
          <label className="label-luxe">Seats taken</label>
          <input type="number" min="0" className="input-luxe w-24" value={seats.taken}
            onChange={(e) => setSeats({ ...seats, taken: parseInt(e.target.value || "0", 10) })} data-testid="seats-taken-input" />
        </div>
        <button className="btn-secondary" onClick={saveSeats} data-testid="save-seats-btn">Save</button>
        <span className="text-xs text-secondary-luxe mb-2" data-testid="seats-preview">
          Shows: "Only {Math.max(seats.total - seats.taken, 0)} spots left"
        </span>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-1" style={{ borderColor: "var(--border)" }}>
        {[
          { k: "weeks", l: "24 Weeks" },
          { k: "cohorts", l: "Cohorts & Members" },
          { k: "replays", l: "Call Replays" },
          { k: "supplements", l: "Supplements & Peptides" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`mtm-tab-${t.k}`}
            className="px-4 py-2 font-label text-[10px] tracking-[0.22em] uppercase border-b-2"
            style={{ borderColor: tab === t.k ? "var(--accent)" : "transparent", color: tab === t.k ? "var(--accent)" : "var(--text-secondary)" }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* WEEKS */}
      {tab === "weeks" && (
        <section className="space-y-4" data-testid="mtm-weeks-tab">
          {["Detox", "Restore", "Burn", "Reset"].map((phase, pi) => (
            <div key={phase}>
              <div className="flex items-baseline gap-4 mt-8 mb-3">
                <span className="font-soft text-2xl" style={{ color: "var(--accent)" }}>0{pi + 1}</span>
                <h3 className="font-display text-xl">{phase}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                {(weeksByPhase[phase] || []).map((w) => {
                  const filled = !!(w.learning || w.nutrition_notes || w.workout_notes);
                  return (
                    <Link key={w.week} to={`/coach/mtm/weeks/${w.week}`} data-testid={`coach-week-${w.week}`}
                      className="p-5 border block transition-colors hover:border-accent"
                      style={{ borderColor: "var(--border-strong)", background: "var(--surface-1)" }}>
                      <div className="flex items-center justify-between">
                        <span className="font-label text-[10px] tracking-widest uppercase" style={{ color: "var(--text-tertiary)" }}>Week {w.week}</span>
                        <span className="font-label text-[9px] tracking-widest uppercase"
                          style={{ color: filled ? "var(--accent)" : "var(--text-tertiary)" }}>
                          {filled ? "Drafted" : "Empty"}
                        </span>
                      </div>
                      <h4 className="font-display text-base mt-3 leading-tight">{w.title}</h4>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* COHORTS */}
      {tab === "cohorts" && (
        <section className="space-y-6" data-testid="mtm-cohorts-tab">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl">Cohorts</h3>
            <button onClick={() => setShowCohort(!showCohort)} className="btn-primary" data-testid="new-cohort-btn">
              <Plus size={12}/> New cohort
            </button>
          </div>
          {showCohort && (
            <form onSubmit={createCohort} className="card-luxe space-y-4 max-w-xl" data-testid="cohort-form">
              <div><label className="label-luxe">Cohort name</label>
                <input className="input-luxe" required value={newCohort.name} onChange={(e) => setNewCohort({ ...newCohort, name: e.target.value })} placeholder="Spring 2026" data-testid="cohort-name" /></div>
              <div><label className="label-luxe">Start date (Week 1, Day 1)</label>
                <input type="date" className="input-luxe" required value={newCohort.start_date} onChange={(e) => setNewCohort({ ...newCohort, start_date: e.target.value })} data-testid="cohort-start" /></div>
              <button className="btn-primary" data-testid="cohort-submit">Create cohort</button>
            </form>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cohorts.map((c) => (
              <div key={c.cohort_id} className="card-luxe" data-testid={`cohort-${c.cohort_id}`}>
                <p className="overline">{c.member_count} members</p>
                <h4 className="font-display text-xl mt-3">{c.name}</h4>
                <div className="font-label text-[10px] tracking-widest uppercase mt-2" style={{ color: "var(--text-tertiary)" }}>
                  <Calendar size={11} className="inline mr-2" />Begins {c.start_date}
                </div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="font-display text-2xl mt-10">Enroll a client</h3>
            <div className="bronze-rule mt-3 mb-5" />
            <div className="space-y-2">
              {clients.map((cl) => (
                <div key={cl.user_id} className="card-luxe flex items-center justify-between gap-4 flex-wrap" data-testid={`enroll-${cl.user_id}`}>
                  <div>
                    <div className="font-medium">{cl.name}</div>
                    <div className="font-label text-[10px] tracking-wider" style={{ color: "var(--text-secondary)" }}>{cl.email}</div>
                  </div>
                  <select className="input-luxe text-xs max-w-[260px]" defaultValue=""
                    onChange={(e) => enroll(cl.user_id, e.target.value)} data-testid={`enroll-select-${cl.user_id}`}>
                    <option value="">Enroll in cohort…</option>
                    {cohorts.map((c) => <option key={c.cohort_id} value={c.cohort_id}>{c.name}</option>)}
                  </select>
                </div>
              ))}
              {clients.length === 0 && <div className="text-secondary-luxe text-sm">No clients yet.</div>}
            </div>
          </div>
        </section>
      )}

      {/* CALL REPLAYS */}
      {tab === "replays" && (
        <section className="space-y-6" data-testid="mtm-replays-tab">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-2xl">Group call replays</h3>
              <p className="text-xs text-secondary-luxe mt-1">Paste your Zoom/YouTube/Vimeo recording links — MTM clients see them in their app.</p>
            </div>
            <button onClick={() => { setShowReplay(!showReplay); setReplayEditId(null); setReplayForm(EMPTY_REPLAY); }} className="btn-primary" data-testid="new-replay-btn">
              <Plus size={12}/> Add replay
            </button>
          </div>
          {showReplay && (
            <form onSubmit={submitReplay} className="card-luxe space-y-4 max-w-2xl" data-testid="replay-form">
              <div className="overline">{replayEditId ? "Edit replay" : "New replay"}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><label className="label-luxe">Title</label>
                  <input className="input-luxe" required value={replayForm.title} onChange={(e) => setReplayForm({ ...replayForm, title: e.target.value })} placeholder="Week 3 Coaching Call — Cravings deep-dive" data-testid="replay-title" /></div>
                <div><label className="label-luxe">Call date</label>
                  <input type="date" className="input-luxe" required value={replayForm.date} onChange={(e) => setReplayForm({ ...replayForm, date: e.target.value })} data-testid="replay-date" /></div>
                <div className="md:col-span-2"><label className="label-luxe">Recording URL (YouTube, Vimeo, Zoom share link…)</label>
                  <input className="input-luxe" required value={replayForm.video_url} onChange={(e) => setReplayForm({ ...replayForm, video_url: e.target.value })} placeholder="https://…" data-testid="replay-url" /></div>
                <div><label className="label-luxe">MTM week (optional)</label>
                  <input type="number" min="1" max="24" className="input-luxe" value={replayForm.week_number} onChange={(e) => setReplayForm({ ...replayForm, week_number: e.target.value })} data-testid="replay-week" /></div>
                <div className="md:col-span-2"><label className="label-luxe">What was covered (optional)</label>
                  <textarea rows={2} className="input-luxe" value={replayForm.description} onChange={(e) => setReplayForm({ ...replayForm, description: e.target.value })} data-testid="replay-description" /></div>
              </div>
              <button className="btn-primary" data-testid="replay-submit">{replayEditId ? "Save changes" : "Add replay"}</button>
            </form>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {replays.map((r) => (
              <div key={r.replay_id} className="card-luxe" data-testid={`replay-${r.replay_id}`}>
                <div className="flex items-center justify-between">
                  <p className="overline flex items-center gap-2"><PlayCircle size={12} style={{ color: "var(--accent)" }} /> {r.date}{r.week_number ? ` · Week ${r.week_number}` : ""}</p>
                  <div className="flex gap-1">
                    <button onClick={() => editReplay(r)} className="btn-ghost" data-testid={`edit-replay-${r.replay_id}`}><Pencil size={12}/></button>
                    <button onClick={() => deleteReplay(r.replay_id)} className="btn-ghost" data-testid={`delete-replay-${r.replay_id}`}><Trash2 size={12}/></button>
                  </div>
                </div>
                <h4 className="font-display text-lg mt-3">{r.title}</h4>
                {r.description && <p className="font-soft text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{r.description}</p>}
                <a href={r.video_url} target="_blank" rel="noreferrer" className="font-label text-[10px] tracking-[0.24em] uppercase mt-3 inline-flex items-center gap-2" style={{ color: "var(--accent)" }}>
                  Open recording <ArrowRight size={11} />
                </a>
              </div>
            ))}
            {replays.length === 0 && <p className="text-secondary-luxe text-sm">No replays yet — add your first call recording.</p>}
          </div>
        </section>
      )}

      {/* SUPPLEMENTS */}
      {tab === "supplements" && (
        <section className="space-y-6" data-testid="mtm-supps-tab">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl">Affiliate library</h3>
            <button onClick={() => setShowSupp(!showSupp)} className="btn-primary" data-testid="new-supp-btn">
              <Plus size={12}/> New item
            </button>
          </div>
          {showSupp && (
            <form onSubmit={createSupp} className="card-luxe space-y-4 max-w-2xl" data-testid="supp-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><label className="label-luxe">Name</label>
                  <input className="input-luxe" required value={newSupp.name} onChange={(e) => setNewSupp({ ...newSupp, name: e.target.value })} data-testid="supp-name" /></div>
                <div><label className="label-luxe">Brand</label>
                  <input className="input-luxe" value={newSupp.brand} onChange={(e) => setNewSupp({ ...newSupp, brand: e.target.value })} /></div>
                <div><label className="label-luxe">Category</label>
                  <select className="input-luxe" value={newSupp.category} onChange={(e) => setNewSupp({ ...newSupp, category: e.target.value })}>
                    <option value="supplement">Supplement</option>
                    <option value="peptide">Peptide</option>
                    <option value="protocol">Protocol</option>
                  </select></div>
                <div><label className="label-luxe">Discount code</label>
                  <input className="input-luxe" value={newSupp.discount_code} onChange={(e) => setNewSupp({ ...newSupp, discount_code: e.target.value })} placeholder="DJ15" /></div>
                <div className="md:col-span-2"><label className="label-luxe">Purpose / notes</label>
                  <textarea rows={2} className="input-luxe" value={newSupp.purpose} onChange={(e) => setNewSupp({ ...newSupp, purpose: e.target.value })} placeholder="When to take, why it matters" /></div>
                <div className="md:col-span-2"><label className="label-luxe">Affiliate URL</label>
                  <input className="input-luxe" required value={newSupp.affiliate_url} onChange={(e) => setNewSupp({ ...newSupp, affiliate_url: e.target.value })} data-testid="supp-url" /></div>
              </div>
              <button className="btn-primary" data-testid="supp-submit">Add</button>
            </form>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {supplements.map((s) => (
              <div key={s.supplement_id} className="card-luxe" data-testid={`supp-${s.supplement_id}`}>
                <div className="flex items-center justify-between">
                  <p className="overline">{s.category}</p>
                  <button onClick={() => deleteSupp(s.supplement_id)} className="btn-ghost"><Trash2 size={12}/></button>
                </div>
                <h4 className="font-display text-lg mt-3">{s.name}</h4>
                {s.brand && <div className="font-label text-[10px] tracking-widest uppercase mt-1" style={{ color: "var(--text-tertiary)" }}>{s.brand}</div>}
                {s.purpose && <p className="font-soft text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{s.purpose}</p>}
                {s.discount_code && <span className="inline-block mt-3 px-2 py-1 font-label text-[10px] tracking-widest uppercase" style={{ background: "rgba(168,152,128,0.12)", color: "var(--accent)" }}>Code: {s.discount_code}</span>}
                <a href={s.affiliate_url} target="_blank" rel="noreferrer" className="font-label text-[10px] tracking-[0.24em] uppercase mt-3 inline-flex items-center gap-2" style={{ color: "var(--accent)" }}>
                  Open link <ArrowRight size={11} />
                </a>
              </div>
            ))}
            {supplements.length === 0 && <p className="text-secondary-luxe text-sm">No items yet — add your first.</p>}
          </div>
        </section>
      )}
    </div>
  );
}
